import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useDiscogsConnection, useSyncStatus } from '../context/DiscogsContext'
import { useTheme } from '../context/ThemeContext'
import { useToast } from '../context/ToastContext'
import {
  useCurrentUserProfile,
  useDisconnectTelegramMutation,
  useTestTelegramMutation,
  useWantlist,
  useCollection,
} from '../hooks'
import { initiateTelegramConnect, connectTelegramWidget } from '../api/tracking'
import axios from '../api/client'
import { cn } from '../utils'
import { buildInviteLink } from '../utils/invite'
import { Button, IconButton, Badge } from '../components/common'
import pageStyles from './PageStyles.module.css'
import styles from './SettingsPage.module.css'

export function SettingsPage() {
  const navigate = useNavigate()
  const { user: authUser, isAdmin, logout: appLogout } = useAuth()
  const {
    user: discogsUser,
    isAuthenticated,
    isLoggingIn,
    login: discogsLogin,
    logout: discogsLogout,
    fetchWantlist,
    fetchCollection,
  } = useDiscogsConnection()
  const { syncProgress } = useSyncStatus()
  const { ids: wantlistIds } = useWantlist()
  const { ids: collectionIds } = useCollection()
  const { theme, setTheme } = useTheme()
  const { showToast } = useToast()

  const { data: userProfile, refetch: refetchProfile } = useCurrentUserProfile()
  const disconnectTelegram = useDisconnectTelegramMutation()
  const testTelegram = useTestTelegramMutation()

  const [telegramState, setTelegramState] = useState<'idle' | 'loading' | 'widget'>('idle')
  const [botUsername, setBotUsername] = useState<string | null>(null)
  const widgetContainerRef = useRef<HTMLDivElement | null>(null)

  // ── Invites ─────────────────────────────────────────────────────────────
  type MyInvite = { id: number; code: string; used_by: number | null; used_by_email: string | null; used_at: string | null; created_at: string }
  const [invites, setInvites] = useState<MyInvite[]>([])
  const [inviteLimit, setInviteLimit] = useState(5)
  const [inviteRemaining, setInviteRemaining] = useState(5)
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const fetchInvites = async () => {
    try {
      const { data } = await axios.get('/users/me/invites')
      if (data.success) {
        setInvites(data.invites || [])
        if (typeof data.limit === 'number') setInviteLimit(data.limit)
        if (typeof data.remaining === 'number') setInviteRemaining(data.remaining)
      }
    } catch { /* ignore */ }
  }

  useEffect(() => { fetchInvites() }, [])

  const handleGenerateInvite = async () => {
    setIsGeneratingInvite(true)
    try {
      const { data } = await axios.post('/users/me/invite')
      if (data.success) {
        try {
          await navigator.clipboard.writeText(buildInviteLink(data.code))
          showToast('Invite link copied', 'success')
        } catch {
          showToast('Invite link generated', 'success')
        }
        await fetchInvites()
      } else {
        showToast(data.error || 'Failed to generate invite', 'error')
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to generate invite', 'error')
    } finally {
      setIsGeneratingInvite(false)
    }
  }

  const copyInviteLink = (code: string) => {
    navigator.clipboard.writeText(buildInviteLink(code))
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 1800)
  }

  const isSyncing = !!syncProgress.type
  const telegramConnected = !!userProfile?.telegram_chat_id

  // Clean up widget global on unmount
  useEffect(() => () => { delete (window as any).onTelegramAuth }, [])

  // Inject Telegram Login Widget script once we have the bot username
  useEffect(() => {
    if (telegramState !== 'widget' || !botUsername || !widgetContainerRef.current) return

    ;(window as any).onTelegramAuth = async (user: any) => {
      delete (window as any).onTelegramAuth
      try {
        const result = await connectTelegramWidget(user)
        if (result.success) {
          await refetchProfile()
          showToast('Telegram connected!', 'success')
        } else {
          showToast(result.error || 'Failed to connect', 'error')
        }
      } catch {
        showToast('Failed to connect', 'error')
      }
      setTelegramState('idle')
    }

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.setAttribute('data-telegram-login', botUsername)
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-onauth', 'onTelegramAuth(user)')
    script.setAttribute('data-request-access', 'write')
    script.async = true

    widgetContainerRef.current.innerHTML = ''
    widgetContainerRef.current.appendChild(script)
  }, [telegramState, botUsername, refetchProfile, showToast])

  const handleSignOut = () => {
    appLogout()
    navigate('/login')
  }

  const handleConnectTelegram = async () => {
    setTelegramState('loading')
    try {
      const result = await initiateTelegramConnect()
      if (!result.success || !result.botUsername) {
        showToast(result.error || 'Could not start Telegram connection', 'error')
        setTelegramState('idle')
        return
      }
      setBotUsername(result.botUsername)
      setTelegramState('widget')
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to initiate Telegram connection', 'error')
      setTelegramState('idle')
    }
  }

  const handleCancelConnect = () => {
    delete (window as any).onTelegramAuth
    setBotUsername(null)
    setTelegramState('idle')
  }

  const handleDisconnectTelegram = async () => {
    try {
      await disconnectTelegram.mutateAsync()
      showToast('Telegram disconnected', 'success')
    } catch {
      showToast('Failed to disconnect', 'error')
    }
  }

  const handleTestTelegram = async () => {
    try {
      const result = await testTelegram.mutateAsync()
      if (result.success) {
        showToast('Test message sent!', 'success')
      } else {
        showToast(result.error || 'Failed to send test', 'error')
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to send test message', 'error')
    }
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <h1>Settings</h1>
      </div>

      <div className={styles.settingsContent}>

        {/* Account */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Account</h2>
          <div className={styles.card}>
            <div className={styles.row}>
              <div className={styles.rowInfo}>
                <div className={styles.rowLabel}>Email</div>
              </div>
              <div className={styles.rowValue}>{authUser?.email}</div>
            </div>
            <div className={styles.row}>
              <div className={styles.rowInfo}>
                <div className={styles.rowLabel}>Role</div>
              </div>
              <Badge tone="accent">{isAdmin ? 'Admin' : 'User'}</Badge>
            </div>
            <div className={styles.signOutRow}>
              <Button variant="danger" icon="bi bi-box-arrow-right" onClick={handleSignOut}>
                Sign out
              </Button>
            </div>
          </div>
        </section>

        {/* Invites */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Invites</h2>
          <div className={styles.card}>
            <div className={styles.inviteHeader}>
              <div className={styles.rowInfo}>
                <div className={styles.rowLabel}>Invite a friend</div>
                <div className={styles.rowDesc}>
                  {inviteRemaining > 0
                    ? `${inviteRemaining} of ${inviteLimit} invite ${inviteRemaining === 1 ? 'link' : 'links'} left`
                    : `You've used all ${inviteLimit} invite links`}
                </div>
              </div>
              <Button
                variant="primary"
                icon={`bi ${isGeneratingInvite ? 'bi-arrow-repeat' : 'bi-plus-lg'}`}
                onClick={handleGenerateInvite}
                disabled={isGeneratingInvite || inviteRemaining <= 0}
              >
                {isGeneratingInvite ? 'Generating…' : 'Generate link'}
              </Button>
            </div>

            {invites.length > 0 && (
              <div className={styles.inviteList}>
                {invites.map(inv => {
                  const used = !!inv.used_by
                  return (
                    <div key={inv.id} className={styles.inviteItem}>
                      <code className={cn(styles.inviteCode, used && styles.inviteCodeUsed)}>{inv.code}</code>
                      <span className={cn(styles.inviteStatus, used ? styles.inviteStatusUsed : styles.inviteStatusOpen)}>
                        {used ? `Used by ${inv.used_by_email ?? '—'}` : 'Available'}
                      </span>
                      {!used && (
                        <IconButton
                          variant="ghost"
                          size="sm"
                          icon={`bi ${copiedCode === inv.code ? 'bi-check2' : 'bi-clipboard'}`}
                          aria-label="Copy invite link"
                          title={copiedCode === inv.code ? 'Copied' : 'Copy link'}
                          onClick={() => copyInviteLink(inv.code)}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>

        {/* Telegram */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Telegram Notifications</h2>
          <div className={styles.card}>
            {telegramConnected ? (
              <>
                <div className={styles.discogsConnected}>
                  <div className={styles.discogsAvatar}>
                    <i className="bi bi-telegram"></i>
                  </div>
                  <div className={styles.discogsInfo}>
                    <div className={styles.discogsUsername}>Connected</div>
                    <div className={styles.discogsStatus}>Watchlist alerts enabled</div>
                  </div>
                  <Button variant="secondary" icon="bi bi-x-lg" onClick={handleDisconnectTelegram} disabled={disconnectTelegram.isPending}>
                    Disconnect
                  </Button>
                </div>
                <div className={styles.syncActions}>
                  <Button
                    variant="accentSoft"
                    icon={`bi ${testTelegram.isPending ? 'bi-hourglass-split' : 'bi-send'}`}
                    onClick={handleTestTelegram}
                    disabled={testTelegram.isPending}
                  >
                    {testTelegram.isPending ? 'Sending…' : 'Send Test Message'}
                  </Button>
                </div>
              </>
            ) : telegramState === 'loading' ? (
              <div className={styles.notConnected}>
                <i className={cn('bi bi-hourglass-split', styles.notConnectedIcon)} style={{ color: 'var(--stonks-accent)' }}></i>
                <div className={styles.notConnectedTitle}>Loading…</div>
              </div>
            ) : telegramState === 'widget' ? (
              <div className={styles.notConnected}>
                <i className={cn('bi bi-telegram', styles.notConnectedIcon)}></i>
                <div className={styles.notConnectedTitle}>Log in with Telegram</div>
                <div className={styles.notConnectedDesc}>
                  Click the button below to authenticate with Telegram.
                </div>
                <div ref={widgetContainerRef} style={{ marginTop: '12px' }} />
                <Button variant="secondary" onClick={handleCancelConnect} style={{ marginTop: '8px' }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className={styles.notConnected}>
                <i className={cn('bi bi-telegram', styles.notConnectedIcon)}></i>
                <div className={styles.notConnectedTitle}>No Telegram linked</div>
                <div className={styles.notConnectedDesc}>
                  Connect Telegram to receive alerts when watched releases drop below your target price.
                </div>
                <Button variant="primary" icon="bi bi-telegram" onClick={handleConnectTelegram}>
                  Connect Telegram
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Discogs */}
        <section className={styles.section} data-tour="discogs-section">
          <h2 className={styles.sectionTitle}>Discogs</h2>
          <div className={styles.card}>
            {isAuthenticated && discogsUser ? (
              <>
                <div className={styles.discogsConnected}>
                  <div className={styles.discogsAvatar}>
                    <i className="bi bi-vinyl"></i>
                  </div>
                  <div className={styles.discogsInfo}>
                    <div className={styles.discogsUsername}>{discogsUser.username}</div>
                    <div className={styles.discogsStatus}>Connected</div>
                  </div>
                  <Button variant="secondary" icon="bi bi-x-lg" onClick={discogsLogout}>
                    Disconnect
                  </Button>
                </div>

                <div className={styles.syncStats}>
                  <div className={styles.syncStat}>
                    <span className={styles.syncStatValue}>{wantlistIds.length.toLocaleString()}</span>
                    <span className={styles.syncStatLabel}>Wantlist</span>
                  </div>
                  <div className={styles.syncStat}>
                    <span className={styles.syncStatValue}>{collectionIds.length.toLocaleString()}</span>
                    <span className={styles.syncStatLabel}>Collection</span>
                  </div>
                </div>

                {isSyncing ? (
                  <div className={styles.syncInProgress}>
                    <i className="bi bi-arrow-repeat"></i>
                    Syncing {syncProgress.type}… page {syncProgress.current} of {syncProgress.total}
                    {syncProgress.totalItems > 0 && ` (${syncProgress.totalItems.toLocaleString()} items)`}
                  </div>
                ) : (
                  <div className={styles.syncActions}>
                    <Button variant="accentSoft" icon="bi bi-arrow-repeat" onClick={fetchWantlist} disabled={isSyncing}>
                      Sync Wantlist
                    </Button>
                    <Button variant="accentSoft" icon="bi bi-arrow-repeat" onClick={fetchCollection} disabled={isSyncing}>
                      Sync Collection
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.notConnected}>
                <i className={cn('bi bi-vinyl', styles.notConnectedIcon)}></i>
                <div className={styles.notConnectedTitle}>No Discogs account linked</div>
                <div className={styles.notConnectedDesc}>
                  Connect your Discogs account to sync your wantlist and collection, and access Discogs marketplace data.
                </div>
                <Button
                  variant="primary"
                  icon={`bi ${isLoggingIn ? 'bi-hourglass-split' : 'bi-vinyl'}`}
                  onClick={discogsLogin}
                  disabled={isLoggingIn}
                >
                  {isLoggingIn ? 'Connecting…' : 'Connect Discogs'}
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Appearance */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Appearance</h2>
          <div className={styles.card}>
            <div className={styles.row}>
              <div className={styles.rowInfo}>
                <div className={styles.rowLabel}>Theme</div>
                <div className={styles.rowDesc}>Choose between dark and light mode</div>
              </div>
              <div className={styles.themeToggleGroup}>
                <button
                  className={cn(styles.themeBtn, theme === 'dark' && styles.themeBtnActive)}
                  onClick={() => setTheme('dark')}
                >
                  <i className="bi bi-moon"></i>
                  Dark
                </button>
                <button
                  className={cn(styles.themeBtn, theme === 'light' && styles.themeBtnActive)}
                  onClick={() => setTheme('light')}
                >
                  <i className="bi bi-sun"></i>
                  Light
                </button>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  )
}
